import React, { useState } from 'react';
import { useGameStore } from '../store/gameStore';
import { getDecksRemaining } from '../utils/deck';
import { isBalancedSystem } from '../utils/countingSystems';
import { EyeIcon, EyeOffIcon } from './Icons';

interface CountDisplayProps {
  compact?: boolean;
  useTableSimDeck?: boolean; // If true, use tableSimDeck state instead of training state
}

export const CountDisplay: React.FC<CountDisplayProps> = ({ compact = false, useTableSimDeck = false }) => {
  const { runningCount, trueCount, deck, settings, dealtCards, submitCount, userInputCount, tableSimDeck, gameMode } = useGameStore();
  const [showCount, setShowCount] = useState(false);
  const [inputCount, setInputCount] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  
  // Use table sim's deck state if specified or if in table-simulator mode
  const shouldUseTableSim = useTableSimDeck || gameMode === 'table-simulator';
  const activeRunningCount = shouldUseTableSim ? tableSimDeck.runningCount : runningCount;
  const activeTrueCount = shouldUseTableSim ? tableSimDeck.trueCount : trueCount;
  const activeDeck = shouldUseTableSim ? tableSimDeck.deck : deck;
  const activeDealtCards = shouldUseTableSim ? tableSimDeck.dealtCards : dealtCards;
  
  const decksRemaining = getDecksRemaining(activeDeck.length);
  const isBalanced = isBalancedSystem(settings.countingSystem);
  
  const handleSubmit = () => {
    const count = parseInt(inputCount);
    if (!isNaN(count)) {
      submitCount(count);
      setShowFeedback(true);
      setTimeout(() => {
        setShowFeedback(false);
        setInputCount('');
      }, 2000);
    }
  };
  
  const isCorrect = userInputCount === activeRunningCount;

  // Compact mode for banner display
  if (compact) {
    return (
      <div className="flex flex-wrap items-center gap-3 sm:gap-4">
        {/* Count Input */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-300 whitespace-nowrap">Your Count:</label>
          <input
            type="number"
            value={inputCount}
            onChange={(e) => setInputCount(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
            placeholder="..."
            className="w-16 sm:w-20 px-2 py-1 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none text-center"
          />
          <button
            onClick={handleSubmit}
            className="group px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg font-semibold transition-colors text-sm flex items-center gap-1.5"
          >
            Check
          </button>
        </div>
        
        {showFeedback && userInputCount !== undefined && (
          <div className={`text-sm font-bold ${isCorrect ? 'text-green-400' : 'text-red-400'}`}>
            {isCorrect ? '✓ Correct!' : `✗ Wrong! (${activeRunningCount})`}
          </div>
        )}
        
        {/* Reveal Button */}
        <button
          onClick={() => setShowCount(!showCount)}
          className="group px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg font-semibold transition-colors text-sm flex items-center gap-1.5"
        >
          {showCount ? (
            <>
              <EyeOffIcon size={16} className="transition-colors group-hover:text-white" />
              Hide
            </>
          ) : (
            <>
              <EyeIcon size={16} className="transition-colors group-hover:text-white" />
              Reveal
            </>
          )}
        </button>
        
        {/* Revealed Count */}
        {showCount && (
          <div className="flex items-center gap-3">
            <div className="bg-blue-600 rounded px-3 py-1">
              <span className="text-xs text-blue-200">RC:</span>
              <span className="ml-1 font-bold text-white">{activeRunningCount > 0 ? '+' : ''}{activeRunningCount}</span>
            </div>
            {isBalanced && (
              <div className="bg-purple-600 rounded px-3 py-1">
                <span className="text-xs text-purple-200">TC:</span>
                <span className="ml-1 font-bold text-white">{activeTrueCount > 0 ? '+' : ''}{activeTrueCount}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
  
  // Full mode for sidebar
  return (
    <div className="bg-gray-800 rounded-xl p-4 sm:p-6 shadow-lg">
      <h3 className="text-lg sm:text-xl font-bold text-white mb-4">Count Tracker</h3>
      
      <div className="space-y-4">
        {/* Deck Info */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-700 rounded-lg p-3">
            <div className="text-gray-400 text-xs sm:text-sm">Cards Dealt</div>
            <div className="text-xl sm:text-2xl font-bold text-white">{activeDealtCards.length}</div>
          </div>
          
          <div className="bg-gray-700 rounded-lg p-3">
            <div className="text-gray-400 text-xs sm:text-sm">Decks Remaining</div>
            <div className="text-xl sm:text-2xl font-bold text-white">{decksRemaining}</div>
          </div>
        </div>
        
        {/* Practice Input */}
        <div className="bg-gray-700 rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Your Running Count:
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              value={inputCount}
              onChange={(e) => setInputCount(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
              placeholder="Enter count..."
              className="flex-1 px-3 py-2 bg-gray-600 text-white rounded border border-gray-500 focus:border-blue-500 focus:outline-none"
            />
            <button
              onClick={handleSubmit}
              className="group px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg font-semibold transition-colors text-sm flex items-center gap-1.5"
            >
              Check
            </button>
          </div>
          
          {showFeedback && userInputCount !== undefined && (
            <div className={`mt-2 text-center font-bold ${isCorrect ? 'text-green-400' : 'text-red-400'}`}>
              {isCorrect ? '✓ Correct!' : `✗ Wrong! Count is ${activeRunningCount}`}
            </div>
          )}
        </div>
        
        {/* Show/Hide Count Toggle */}
        <button
          onClick={() => setShowCount(!showCount)}
          className="w-full group px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg font-semibold transition-colors text-sm flex items-center justify-center gap-1.5"
        >
          {showCount ? (
            <>
              <EyeOffIcon size={16} className="transition-colors group-hover:text-white" />
              Hide Count
            </>
          ) : (
            <>
              <EyeIcon size={16} className="transition-colors group-hover:text-white" />
              Reveal Count
            </>
          )}
        </button>
        
        {/* Actual Count Display */}
        {showCount && (
          <div className="space-y-3 animate-slide-in">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-4">
              <div className="text-gray-200 text-sm mb-1">Running Count</div>
              <div className="text-3xl font-bold text-white">
                {activeRunningCount > 0 ? '+' : ''}{activeRunningCount}
              </div>
            </div>
            
            {isBalanced && (
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg p-4">
                <div className="text-gray-200 text-sm mb-1">True Count</div>
                <div className="text-3xl font-bold text-white">
                  {activeTrueCount > 0 ? '+' : ''}{activeTrueCount}
                </div>
                <div className="text-xs text-gray-300 mt-1">
                  Running: {activeRunningCount} ÷ Decks: {decksRemaining}
                </div>
              </div>
            )}
            
            {!isBalanced && (
              <div className="bg-gray-700 rounded-lg p-3">
                <div className="text-xs text-gray-400">
                  Using unbalanced system - no true count conversion needed
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Count Advantage Indicator */}
        {showCount && (
          <div className="bg-gray-700 rounded-lg p-3">
            <div className="text-sm text-gray-300 mb-2">Player Advantage</div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-gray-600 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    activeTrueCount > 0 ? 'bg-green-500' : activeTrueCount < 0 ? 'bg-red-500' : 'bg-yellow-500'
                  }`}
                  style={{
                    width: `${Math.min(100, Math.abs(activeTrueCount) * 20)}%`
                  }}
                />
              </div>
              <div className={`text-sm font-bold ${
                activeTrueCount > 2 ? 'text-green-400' : activeTrueCount < -1 ? 'text-red-400' : 'text-yellow-400'
              }`}>
                {activeTrueCount > 2 ? 'Good' : activeTrueCount < -1 ? 'Bad' : 'Neutral'}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
